import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

@Composable
fun TeamPlayersStatsPanel(games: List<StatGame>, details: List<DetailedGame>, names: Map<String, String>, selectedEvent: String? = null) {
    var visiblePlayers by remember { mutableStateOf(games.map { it.playerId }.toSet()) }
    var showTeamAvg by remember { mutableStateOf(true) }
    LaunchedEffect(games) { visiblePlayers = visiblePlayers + games.map { it.playerId } }
    val eventMode = selectedEvent != null
    val displayed = filterStatEvent(games, selectedEvent)
    if (displayed.isEmpty()) {
        P(attrs = { classes("muted") }) { Text("Vyber událost pro porovnání hráčů.") }
        return
    }
    val displayedDetails = seasonComparisonGames(displayed, details)
    val seasonDetails = seasonComparisonGames(games, details)
    val playerOrder = displayed.groupBy { it.playerId }.entries
        .sortedByDescending { it.value.map { g -> g.score }.average() }.map { it.key }
    val seasonByPlayer = games.groupBy { it.playerId }
    val metricsByPlayer = displayedDetails.groupBy { it.game.playerId }.mapValues { bowlingMetrics(it.value) }
    val seasonMetrics = seasonDetails.groupBy { it.game.playerId }.mapValues { bowlingMetrics(it.value) }

    H4 { Text("Porovnání hráčů") }
    BarChart(playerOrder.map { pid ->
        val pg = displayed.filter { it.playerId == pid }
        Triple(names[pid] ?: pid, round1(pg.map { it.score }.average()), pg.size)
    })
    if (eventMode) {
        H4 { Text("Porovnání náhozů se sezónou") }
        P(attrs = { classes("muted") }) {
            Text("Sezónní průměry hráčů vycházejí z filtrů sezóny, zdrojů a typu a zahrnují i vybranou událost. Při přepínání událostí se nemění; při filtru Všechny sezóny zahrnují celé zvolené období.")
        }
        Div(attrs = { classes("stats-table-wrap") }) {
            Table(attrs = { classes("stats-table", "player-season-comparison") }) {
                Thead { Tr { listOf("Hráč", "Událost Ø", "Sezóna Ø", "Rozdíl", "Her / sezóna").forEach { Th { Text(it) } } } }
                Tbody {
                    playerOrder.forEach { pid ->
                        val event = displayed.filter { it.playerId == pid }
                        val season = seasonByPlayer[pid].orEmpty()
                        val avg = event.map { it.score }.average()
                        val baseline = season.map { it.score }.average()
                        Tr {
                            Td { Text(names[pid] ?: pid) }
                            Td { Text("${round1(avg)}") }
                            Td { Text("${round1(baseline)}") }
                            Td { Text(signedDifference(avg, baseline)) }
                            Td { Text("${event.size} / ${season.size}") }
                        }
                    }
                }
            }
        }
    }

    H4 { Text("Porovnání hráčů v čase") }
    Div(attrs = { classes("player-toggles") }) {
        Label(attrs = { classes("cms-check") }) {
            CheckboxInput(checked = showTeamAvg) { onChange { showTeamAvg = it.value } }
            Span(attrs = { classes("legend-dot"); attr("style", "background:$TEAM_AVG_COLOR") }) {}
            Text(" Průměr týmu")
        }
        playerOrder.forEachIndexed { index, pid ->
            Label(attrs = { classes("cms-check") }) {
                CheckboxInput(checked = pid in visiblePlayers) { onChange { visiblePlayers = visiblePlayers.toggle(pid) } }
                Span(attrs = { classes("legend-dot"); attr("style", "background:${playerColor(index)}") }) {}
                Text(" ${names[pid] ?: pid}")
            }
        }
    }
    MultiTimeChart(buildList {
        if (showTeamAvg) add(ChartSeries("Průměr týmu", TEAM_AVG_COLOR, timePoints(displayed), emphasize = true))
        playerOrder.forEachIndexed { index, pid ->
            if (pid in visiblePlayers) add(ChartSeries(names[pid] ?: pid, playerColor(index), timePoints(displayed.filter { it.playerId == pid })))
        }
    })
    H4 { Text("Frameové ukazatele hráčů") }
    Div(attrs = { classes("stats-table-wrap") }) {
        Table(attrs = { classes("stats-table", "player-frame-comparison") }) {
            Thead { Tr { listOf("Hráč", "Strike", "Spare", "Open", "Zápisy").forEach { Th { Text(it) } } } }
            Tbody {
                playerOrder.forEach { pid ->
                    val m = metricsByPlayer[pid] ?: bowlingMetrics(emptyList())
                    val s = if (eventMode) seasonMetrics[pid] else null
                    Tr {
                        Td { Text(names[pid] ?: pid) }
                        PlayerRateCell(m.strikes, m.firstBallAttempts, s?.let { it.strikes to it.firstBallAttempts })
                        PlayerRateCell(m.spares, m.spareOpportunities, s?.let { it.spares to it.spareOpportunities })
                        PlayerRateCell(m.opens, m.resolvedFrames, s?.let { it.opens to it.resolvedFrames })
                        Td { Text("${m.games}") }
                    }
                }
            }
        }
    }
    if (eventMode) P(attrs = { classes("muted") }) { Text("U frameových ukazatelů je pod výsledkem sezónní průměr hráče a rozdíl v procentních bodech. Pomlčka znamená chybějící zápis nebo žádnou příležitost.") }
    if (eventMode) CumulativeScoreChart(displayedDetails, seasonDetails, names)
}

@Composable
private fun PlayerRateCell(part: Int, total: Int, season: Pair<Int, Int>?) {
    Td {
        Text(percentage(part, total))
        season?.let { (seasonPart, seasonTotal) ->
            Div(attrs = { classes("muted") }) {
                Text("Sezóna ${percentage(seasonPart, seasonTotal)}")
                if (total > 0 && seasonTotal > 0) Text(" · ${signedDifference(part * 100.0 / total, seasonPart * 100.0 / seasonTotal)} p. b.")
            }
        }
    }
}
